import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Send, ListChecks } from "lucide-react";

export default function AssignmentsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title:"", subject:"", max_marks:20, due_date: new Date(Date.now()+7*86400000).toISOString().slice(0,10), batch:"" });

  const [submitFor, setSubmitFor] = useState(null); // assignment obj
  const [content, setContent] = useState("");
  const [attachment, setAttachment] = useState("");

  const [viewSubs, setViewSubs] = useState(null); // assignment obj
  const [subsList, setSubsList] = useState([]);
  const [evalOpen, setEvalOpen] = useState(null);
  const [marks, setMarks] = useState(0);
  const [remarks, setRemarks] = useState("");

  const load = async () => setItems((await api.get("/assignments")).data);
  useEffect(()=>{load()},[]);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/assignments", { ...form, max_marks:Number(form.max_marks) });
    setOpen(false); toast.success("Assignment created"); load();
  };

  const doSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/assignments/${submitFor.id}/submit`, { content, attachment_url: attachment });
      setSubmitFor(null); setContent(""); setAttachment("");
      toast.success("Submitted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    }
  };

  const openSubs = async (a) => {
    const { data } = await api.get(`/assignments/${a.id}/submissions`);
    setViewSubs(a); setSubsList(data);
  };

  const doEval = async (e) => {
    e.preventDefault();
    await api.put(`/submissions/${evalOpen.id}/evaluate`, { marks:Number(marks), remarks });
    toast.success("Evaluated");
    setEvalOpen(null); setMarks(0); setRemarks("");
    if (viewSubs) openSubs(viewSubs);
  };

  const canCreate = ["super_admin","branch_admin","faculty"].includes(user?.role);
  const canSubmit = user?.role === "student";
  const canEval = ["super_admin","branch_admin","faculty"].includes(user?.role);

  return (
    <div>
      <PageHeader
        title="Assignments"
        subtitle="Homework, submissions and evaluation"
        actions={canCreate ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><button className="btn-primary" data-testid="add-assignment-btn">+ New assignment</button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New assignment</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <input required placeholder="Title" data-testid="assignment-title-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.title} onChange={e=>setForm({...form, title:e.target.value})}/>
                <div className="grid grid-cols-2 gap-2">
                  <input placeholder="Subject" data-testid="assignment-subject-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.subject} onChange={e=>setForm({...form, subject:e.target.value})}/>
                  <input type="number" placeholder="Max marks" data-testid="assignment-marks-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.max_marks} onChange={e=>setForm({...form, max_marks:e.target.value})}/>
                </div>
                <input type="date" data-testid="assignment-due-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.due_date} onChange={e=>setForm({...form, due_date:e.target.value})}/>
                <input placeholder="Batch" data-testid="assignment-batch-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.batch} onChange={e=>setForm({...form, batch:e.target.value})}/>
                <DialogFooter><button className="btn-primary" data-testid="assignment-save-btn">Save</button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        ) : null}
      />
      <DataTable
        testid="assignments-table"
        columns={[
          { key:"title", label:"Title" },
          { key:"subject", label:"Subject" },
          { key:"batch", label:"Batch" },
          { key:"due_date", label:"Due" },
          { key:"max_marks", label:"Marks" },
          { key:"actions", label:"", render: r => (
            <div className="flex gap-2">
              {canSubmit && <button className="text-xs px-3 py-1 rounded-full bg-[#0A66C2]/10 text-[#0A66C2] hover:bg-[#0A66C2]/20 font-semibold inline-flex items-center gap-1" data-testid={`submit-btn-${r.id}`} onClick={()=>setSubmitFor(r)}><Send size={12}/> Submit</button>}
              {canEval && <button className="text-xs px-3 py-1 rounded-full bg-[#EDEBE7] text-[#000000E6] hover:bg-[#E0DFDC] font-semibold inline-flex items-center gap-1" data-testid={`view-subs-btn-${r.id}`} onClick={()=>openSubs(r)}><ListChecks size={12}/> Submissions</button>}
            </div>
          )},
        ]}
        rows={items}
      />

      {/* Submit dialog */}
      <Dialog open={!!submitFor} onOpenChange={(v)=>!v && setSubmitFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Submit: {submitFor?.title}</DialogTitle></DialogHeader>
          <form onSubmit={doSubmit} className="space-y-3">
            <textarea required placeholder="Your answer / solution" data-testid="submission-content-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md min-h-[140px]" value={content} onChange={e=>setContent(e.target.value)}/>
            <input placeholder="Attachment URL (optional)" data-testid="submission-attachment-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={attachment} onChange={e=>setAttachment(e.target.value)}/>
            <DialogFooter><button className="btn-primary" data-testid="submission-submit-btn">Submit assignment</button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Submissions list */}
      <Dialog open={!!viewSubs} onOpenChange={(v)=>!v && setViewSubs(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Submissions: {viewSubs?.title}</DialogTitle></DialogHeader>
          <div className="divide-y divide-[#E0DFDC]" data-testid="submissions-list">
            {subsList.length === 0 && <div className="text-center text-sm text-[#00000099] py-6">No submissions yet</div>}
            {subsList.map(s => (
              <div key={s.id} className="py-3" data-testid={`sub-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm">{s.student_name}</div>
                    <div className="text-xs text-[#00000099]">{new Date(s.created_at).toLocaleString()}</div>
                    <div className="text-sm text-[#000000E6] mt-1 whitespace-pre-wrap">{s.content}</div>
                    {s.attachment_url && <a href={s.attachment_url} target="_blank" rel="noreferrer" className="text-xs text-[#0A66C2] underline mt-1 inline-block">Attachment</a>}
                    {s.status === "evaluated" && (
                      <div className="mt-2 p-2 bg-[#EAF3FB] rounded text-xs">
                        <div className="font-semibold text-[#0A66C2]">Score: {s.marks} / {viewSubs.max_marks}</div>
                        {s.remarks && <div className="text-[#000000E6] mt-1">&ldquo;{s.remarks}&rdquo;</div>}
                      </div>
                    )}
                  </div>
                  {canEval && s.status !== "evaluated" && (
                    <button className="text-xs px-3 py-1 rounded-full bg-[#0A66C2] text-white font-semibold" data-testid={`eval-btn-${s.id}`} onClick={()=>{ setEvalOpen(s); setMarks(0); setRemarks(""); }}>Evaluate</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Evaluate dialog */}
      <Dialog open={!!evalOpen} onOpenChange={(v)=>!v && setEvalOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Evaluate submission</DialogTitle></DialogHeader>
          <form onSubmit={doEval} className="space-y-3">
            <input type="number" step="0.5" required placeholder="Marks" data-testid="eval-marks-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={marks} onChange={e=>setMarks(e.target.value)}/>
            <textarea placeholder="Remarks" data-testid="eval-remarks-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md" value={remarks} onChange={e=>setRemarks(e.target.value)}/>
            <DialogFooter><button className="btn-primary" data-testid="eval-save-btn">Save evaluation</button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
